import { StrictMode, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { createRoot } from 'react-dom/client';
import { HashRouter, NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ApiError, api, ensureSession, currentSession } from './lib/api.js';
import { applyTheme, tg } from './lib/telegram.js';
import { I18nContext, createTranslator, type Locale } from './lib/i18n.js';
import { ErrorBox, Spinner } from './components/ui.js';
import { HomePage } from './pages/Home.js';
import { IssuesPage } from './pages/Issues.js';
import { IssueDetailPage } from './pages/IssueDetail.js';
import { CreatePage } from './pages/Create.js';
import { AgilePage } from './pages/Agile.js';
import { JsmPage } from './pages/Jsm.js';
import { AssetsPage } from './pages/Assets.js';
import { AccessPage } from './pages/Access.js';
import { NotificationsPage } from './pages/Notifications.js';
import { SettingsPage } from './pages/Settings.js';
import { AdminPage } from './pages/Admin.js';
import { PowerToolsPage } from './pages/PowerTools.js';
import './styles/app.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // Permission and connection failures never succeed on retry.
          const terminal = [
            'JiraForbidden',
            'JiraNotFound',
            'NotConnected',
            'ReauthRequired',
            'SiteNotSelected',
            'FeatureDisabled',
            'BotPolicyDenied',
            'ProviderUnsupported',
          ];
          if (terminal.includes(error.code)) return false;
        }
        return failureCount < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function TabBar(): JSX.Element {
  const t = createTranslator(document.documentElement.lang as Locale);
  const tabs = [
    { to: '/', icon: '🏠', label: t.t('home.title').replace(/<[^>]+>/g, ''), end: true },
    { to: '/issues', icon: '📋', label: t.t('common.search') },
    { to: '/agile', icon: '🏃', label: t.t('agile.title') },
    { to: '/notifications', icon: '🔔', label: t.t('notify.title').replace(/<[^>]+>/g, '') },
    { to: '/settings', icon: '⚙️', label: t.t('settings.title').replace(/<[^>]+>/g, '') },
  ];
  return (
    <nav className="tabbar">
      {tabs.map((tab) => (
        <NavLink key={tab.to} to={tab.to} end={tab.end}>
          <span className="icon">{tab.icon}</span>
          <span>{tab.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function BackButtonBridge(): null {
  const navigate = useNavigate();
  useEffect(() => {
    const app = tg();
    if (!app) return;
    const onBack = () => navigate(-1);
    const update = () => {
      if (window.location.hash && window.location.hash !== '#/') app.BackButton.show();
      else app.BackButton.hide();
    };
    app.BackButton.onClick(onBack);
    update();
    window.addEventListener('hashchange', update);
    return () => {
      app.BackButton.offClick(onBack);
      window.removeEventListener('hashchange', update);
    };
  }, [navigate]);
  return null;
}

function Shell(): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>('fa');
  const [ready, setReady] = useState(false);
  const [bootError, setBootError] = useState<unknown>(null);

  useEffect(() => {
    const app = tg();
    app?.ready();
    app?.expand();
    applyTheme();
    app?.onEvent('themeChanged', applyTheme);

    ensureSession()
      .then((session) => {
        setLocaleState(session.user.locale);
        document.documentElement.lang = session.user.locale;
        document.documentElement.dir = session.user.locale === 'fa' ? 'rtl' : 'ltr';
        setReady(true);
      })
      .catch((error) => {
        setBootError(error);
        setReady(true);
      });

    return () => app?.offEvent('themeChanged', applyTheme);
  }, []);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    document.documentElement.lang = next;
    document.documentElement.dir = next === 'fa' ? 'rtl' : 'ltr';
  }, []);

  const translator = useMemo(() => createTranslator(locale), [locale]);

  const value = useMemo(
    () => ({
      locale,
      dir: translator.dir,
      t: translator.t,
      has: translator.has,
      setLocale,
    }),
    [locale, translator, setLocale],
  );

  if (!ready) return <Spinner />;

  return (
    <I18nContext.Provider value={value}>
      <HashRouter>
        <BackButtonBridge />
        <div className="app">
          {bootError ? (
            <div className="page">
              <ErrorBox error={bootError} />
            </div>
          ) : (
            <ConnectionGate />
          )}
        </div>
        {!bootError ? <TabBar /> : null}
      </HashRouter>
    </I18nContext.Provider>
  );
}

function ConnectionGate(): JSX.Element {
  const session = currentSession();
  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => api<{ activeSite: { tenantId: string } | null }>('/me'),
  });

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <div className="page">
        <ErrorBox error={error} />
      </div>
    );
  }

  if (!data?.activeSite) {
    return <NotConnected connected={session?.connected ?? false} />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/issues" element={<IssuesPage />} />
      <Route path="/issues/:issueKey" element={<IssueDetailPage />} />
      <Route path="/create" element={<CreatePage />} />
      <Route path="/agile" element={<AgilePage />} />
      <Route path="/jsm" element={<JsmPage />} />
      <Route path="/assets" element={<AssetsPage />} />
      <Route path="/access" element={<AccessPage />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/power" element={<PowerToolsPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function NotConnected({ connected }: { connected: boolean }): JSX.Element {
  const t = createTranslator(document.documentElement.lang as Locale);
  return (
    <div className="page">
      <div className="card">
        <h1 style={{ fontSize: 18, marginTop: 0 }}>{t.t('app.name')}</h1>
        <p className="muted">
          {connected ? t.t('error.SiteNotSelected') : t.t('start.not_connected')}
        </p>
        <p className="small muted">{t.t('help.security')}</p>
        <button
          type="button"
          className="btn btn-block"
          onClick={() => tg()?.close()}
        >
          {t.t('common.close')}
        </button>
      </div>
    </div>
  );
}

const root = document.getElementById('root');
if (root) {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <Shell />
      </QueryClientProvider>
    </StrictMode>,
  );
}
